// src/pages/dashboard/DashboardWarek.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckSquare,
  AlertTriangle,
  TrendingUp,
  ChevronRight,
  AlertCircle,
  Gavel,
  Users,
  Building2,
  Hourglass,
  Target,
  ArrowUpRight,
  Clock,
  Calendar,
  BarChart3,
  ExternalLink,
} from 'lucide-react';
import { Card, Badge, Breadcrumb, Select, ProgressBar, Loading,
} from '../../components/ui';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from 'recharts';
import api from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { TingkatRisiko } from '../../types';

// ========== CONSTANTS ==========
const STATUS_COLORS = ['#F59E0B', '#F97316', '#3B82F6', '#22C55E', '#8B5CF6', '#EF4444'];

// ========== TYPES ==========
interface KpiData {
  menunggu_validasi: number;
  total_laporan_bidang: number;
  antrean_rapim: number;
  selesai: number;
  overdue: number;
  menunggu_konfirmasi: number;
  progress_ppepp: number;
  rata_rata_penyelesaian: number;
}

interface PendingItem {
  id: number;
  kode: string;
  judul: string;
  status: string;
  tingkat_risiko?: string;
  risiko?: string;
  unit?: { id: number; name: string };
  user?: { id: number; name: string };
  kategori_masalah?: { id: number; nama: string };
  created_at?: string;
}

interface FakultasItem {
  unit_id?: number;
  unit?: string;
  name?: string;
  total: number;
  selesai: number;
  overdue: number;
  completion_rate?: number;
}

interface Periode {
  id: number;
  nama: string;
  is_active?: boolean;
}

// ========== COMPONENT ==========
const DashboardWarek: React.FC = () => {
  const { user } = useAuthStore();
  const warekRole = user?.role?.display_name || 'Wakil Rektor';

  // States
  const [loading, setLoading] = useState(true);
  const [kpiData, setKpiData] = useState<KpiData>({
    menunggu_validasi: 0,
    total_laporan_bidang: 0,
    antrean_rapim: 0,
    selesai: 0,
    overdue: 0,
    menunggu_konfirmasi: 0,
    progress_ppepp: 0,
    rata_rata_penyelesaian: 0,
  });
  const [pendingValidasi, setPendingValidasi] = useState<PendingItem[]>([]);
  const [antreanRapim, setAntreanRapim] = useState<PendingItem[]>([]);
  const [statusData, setStatusData] = useState<any[]>([]);
  const [fakultasData, setFakultasData] = useState<FakultasItem[]>([]);
  const [scopeBidang, setScopeBidang] = useState<string[]>([]);
  const [periodeList, setPeriodeList] = useState<Periode[]>([]);
  const [selectedPeriode, setSelectedPeriode] = useState<string>('');
  const [isPeriodeReady, setIsPeriodeReady] = useState(false);

  // ========== FETCH PERIODE ==========
  useEffect(() => {
    const fetchPeriode = async () => {
      try {
        const periodeRes = await api.get('/admin/periode').catch(() => ({ data: { data: [] } }));
        const periodeData = periodeRes.data?.data || periodeRes.data || [];
        const normalizedPeriode = Array.isArray(periodeData) ? periodeData : [];
        setPeriodeList(normalizedPeriode);

        const activePeriode = normalizedPeriode.find((p: Periode) => p.is_active);
        setSelectedPeriode(activePeriode ? String(activePeriode.id) : '');
      } catch {
        setPeriodeList([]);
        setSelectedPeriode('');
      } finally {
        setIsPeriodeReady(true);
      }
    };
    fetchPeriode();
  }, []);

  // ========== FETCH DATA ==========
  useEffect(() => {
    if (!isPeriodeReady) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        const params: Record<string, any> = {};
        if (selectedPeriode) params.periode_id = selectedPeriode;

        const [dashboardRes, validasiRes, rapimRes, fakultasRes] = await Promise.all([
          api.get('/dashboard', { params }),
          api.get('/validasi/level2', { params: { per_page: 5, status: 'validasi_l1', ...params } }).catch(() => ({
            data: { data: [] },
          })),
          api.get('/rapim/available-laporan', { params: { per_page: 10, ...params } }).catch(() => ({
            data: { data: [] },
          })),
          api.get('/monitoring/unit-performance', { params }).catch(() => ({
            data: { data: [] },
          })),
        ]);

        // Parse dashboard
        const d = dashboardRes.data?.data || dashboardRes.data;
        if (d) {
          const laporanPerStatus = d.laporan_per_status || {};

          setKpiData({
            menunggu_validasi: laporanPerStatus.validasi_l1 || 0,
            total_laporan_bidang: d.total_laporan || 0,
            antrean_rapim: laporanPerStatus.antrean_rapim || 0,
            selesai: (laporanPerStatus.selesai || 0) + (laporanPerStatus.diarsipkan || 0),
            overdue: d.laporan_overdue || 0,
            menunggu_konfirmasi: laporanPerStatus.menunggu_konfirmasi || 0,
            progress_ppepp: d.progress_ppepp || 0,
            rata_rata_penyelesaian: d.rata_rata_penyelesaian || 0,
          });

          const statusItems = [
            { status: 'Validasi L2', jumlah: laporanPerStatus.validasi_l2 || 0 },
            { status: 'Antrean RAPIM', jumlah: laporanPerStatus.antrean_rapim || 0 },
            { status: 'Berlangsung', jumlah: laporanPerStatus.berlangsung || 0 },
            { status: 'Selesai', jumlah: (laporanPerStatus.selesai || 0) + (laporanPerStatus.diarsipkan || 0) },
          ];
          setStatusData(statusItems.filter(item => item.jumlah > 0));

          const kategoriScope = Array.isArray(d.kategori_scope)
            ? d.kategori_scope.filter((item: any) => typeof item === 'string' && item.trim().length > 0)
            : [];
          setScopeBidang(kategoriScope);
        }

        // Parse validasi
        const validasiData = validasiRes.data?.data || validasiRes.data;
        const validasiList = Array.isArray(validasiData) ? validasiData : (validasiData?.data || []);
        setPendingValidasi(validasiList.filter((lap: PendingItem) => lap.status === 'validasi_l2').slice(0, 5));

        // Parse RAPIM - ambil lebih banyak untuk ditampilkan
        const rapimData = rapimRes.data?.data || rapimRes.data || [];
        setAntreanRapim(Array.isArray(rapimData) ? rapimData : []);

        // Parse fakultas - ambil semua untuk scrollable list
        const fakultasPayload = fakultasRes.data?.data || fakultasRes.data || [];
        if (Array.isArray(fakultasPayload)) {
          const normalizedFakultas = fakultasPayload
            .map((item: any) => ({
              unit_id: item.unit_id,
              unit: item.unit || item.name || '-',
              total: item.total || 0,
              selesai: item.selesai || 0,
              overdue: item.overdue || 0,
              completion_rate: item.total > 0 ? Math.round((item.selesai / item.total) * 100) : 0,
            }))
            .sort((a: FakultasItem, b: FakultasItem) => (b.completion_rate || 0) - (a.completion_rate || 0));
          setFakultasData(normalizedFakultas);
        }

      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedPeriode, isPeriodeReady]);

  // ========== HELPERS ==========
  const formatDate = (dateStr?: string | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getDaysAgo = (dateStr?: string | null): string => {
    if (!dateStr) return '-';
    const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Hari ini';
    if (days === 1) return 'Kemarin';
    return `${days} hari lalu`;
  };

  // ========== LOADING ==========
  if (loading) {
    return <Loading variant="page" message="Memuat data dashboard..." />;
  }

  // ========== COMPUTED ==========
  const periodeOptions = [
    { value: '', label: 'Semua Periode' },
    ...periodeList.map((p) => ({
      value: String(p.id),
      label: `${p.nama}${p.is_active ? ' (Aktif)' : ''}`,
    })),
  ];

  const statusChartData = statusData.filter((item) => Number(item?.jumlah || 0) > 0);

  // Summary stats
  const totalFakultas = fakultasData.length;
  const avgCompletionRate = fakultasData.length > 0
    ? Math.round(fakultasData.reduce((sum, f) => sum + (f.completion_rate || 0), 0) / fakultasData.length)
    : 0;

  // ========== RENDER ==========
  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Dashboard' }, { label: warekRole }]} />

      {/* ===== HEADER ===== */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">
            Dashboard {warekRole}
          </h1>
          <p className="text-sm text-neutral-text-secondary mt-1">
            <Building2 className="w-4 h-4 inline mr-1" />
            Bidang:{' '}
            <span className="font-semibold text-teal-main">
              {scopeBidang.length > 0 ? scopeBidang.join(', ') : 'Semua Kategori'}
            </span>
          </p>
        </div>
        <Select
          options={periodeOptions}
          value={selectedPeriode}
          onChange={setSelectedPeriode}
          className="w-full sm:w-56"
        />
      </div>

      {/* ===== ALERTS ===== */}
      {(kpiData.overdue > 0 || kpiData.menunggu_validasi > 0 || kpiData.menunggu_konfirmasi > 0) && (
        <div className="flex flex-wrap gap-3">
          {kpiData.menunggu_validasi > 0 && (
            <div className="flex-1 min-w-[200px] p-3 bg-warning/5 border border-warning/20 rounded-xl flex items-center gap-3">
              <CheckSquare className="w-5 h-5 text-warning shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-warning">
                  {kpiData.menunggu_validasi} laporan menunggu validasi Level 2
                </p>
              </div>
              <Link to="/validasi/level2" className="text-xs font-semibold text-warning hover:underline shrink-0">
                Validasi →
              </Link>
            </div>
          )}
          {kpiData.overdue > 0 && (
            <div className="flex-1 min-w-[200px] p-3 bg-danger/5 border border-danger/20 rounded-xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-danger shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-danger">
                  {kpiData.overdue} laporan melewati deadline
                </p>
              </div>
              <Link to="/laporan?status=overdue" className="text-xs font-semibold text-danger hover:underline shrink-0">
                Lihat →
              </Link>
            </div>
          )}
        </div>
      )}

      {/* ===== KPI CARDS ===== */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { title: 'Validasi L2', value: kpiData.menunggu_validasi, icon: <CheckSquare className="w-5 h-5" />, color: 'text-warning', bg: 'bg-warning/10', link: '/validasi/level2' },
          { title: 'Antrean RAPIM', value: kpiData.antrean_rapim, icon: <Gavel className="w-5 h-5" />, color: 'text-purple-600', bg: 'bg-purple-100', link: '/rapim' },
          { title: 'Konfirmasi', value: kpiData.menunggu_konfirmasi, icon: <Hourglass className="w-5 h-5" />, color: 'text-amber-600', bg: 'bg-amber-100' },
          { title: 'Terlambat', value: kpiData.overdue, icon: <AlertTriangle className="w-5 h-5" />, color: 'text-danger', bg: 'bg-danger/10' },
          { title: 'Selesai', value: kpiData.selesai, icon: <TrendingUp className="w-5 h-5" />, color: 'text-success', bg: 'bg-success/10' },
          { title: 'Progress', value: `${kpiData.progress_ppepp}%`, icon: <Target className="w-5 h-5" />, color: 'text-info', bg: 'bg-info/10' },
        ].map((kpi, i) => {
          const content = (
            <Card hover={!!kpi.link} className="h-full">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                    {kpi.title}
                  </p>
                  <p className={`text-2xl font-black mt-1 ${kpi.color}`}>{kpi.value}</p>
                </div>
                <div className={`p-2 rounded-lg ${kpi.bg} ${kpi.color}`}>{kpi.icon}</div>
              </div>
            </Card>
          );
          return kpi.link ? <Link key={i} to={kpi.link}>{content}</Link> : <div key={i}>{content}</div>;
        })}
      </div>

      {/* ===== MAIN GRID: Validasi + Status ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pending Validasi L2 */}
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
              <CheckSquare className="w-5 h-5 text-warning" />
              Menunggu Validasi Level 2
            </h3>
            <Link to="/validasi/level2" className="text-xs font-semibold text-teal-main hover:underline flex items-center gap-1">
              Lihat Semua <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-2">
            {pendingValidasi.length > 0 ? (
              pendingValidasi.map((lap, i) => (
                <Link
                  key={lap.id || i}
                  to={`/laporan/${lap.id}`}
                  state={{ fromValidation: true, level: 2 }}
                  className="flex items-center gap-3 p-3 rounded-xl bg-neutral-bg/50 hover:bg-warning/5 border border-transparent hover:border-warning/30 transition-all group"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="text-xs font-mono text-teal-main">{lap.kode}</span>
                      <Badge variant="risk" risk={(lap.tingkat_risiko || lap.risiko || 'sedang') as TingkatRisiko} />
                      {lap.kategori_masalah?.nama && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded text-neutral-text-secondary">
                          {lap.kategori_masalah.nama}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-medium truncate">{lap.judul}</p>
                    <p className="text-xs text-neutral-text-secondary">{lap.unit?.name} • {lap.user?.name}</p>
                  </div>
                  <button className="px-3 py-1.5 text-xs font-semibold text-white bg-warning rounded-lg hover:bg-warning/90 transition-colors opacity-0 group-hover:opacity-100">
                    Review
                  </button>
                </Link>
              ))
            ) : (
              <div className="text-center py-8">
                <CheckSquare className="w-10 h-10 mx-auto mb-2 text-success opacity-50" />
                <p className="text-sm text-neutral-text-secondary">Tidak ada laporan menunggu validasi</p>
              </div>
            )}
          </div>
        </Card>

        {/* Status Distribution */}
        <Card>
          <h3 className="text-base font-bold text-teal-dark mb-4">Distribusi Status</h3>
          {statusChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={statusChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={3}
                  dataKey="jumlah"
                  nameKey="status"
                >
                  {statusChartData.map((_, index) => (
                    <Cell key={index} fill={STATUS_COLORS[index % STATUS_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value} laporan`, '']} />
                <Legend
                  verticalAlign="bottom"
                  formatter={(value: string) => (
                    <span className="text-xs text-neutral-text-secondary">{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-sm text-neutral-text-secondary">
              Belum ada data status
            </div>
          )}
        </Card>
      </div>

      {/* ===== ANTREAN RAPIM (REDESIGNED) ===== */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Gavel className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="text-base font-bold text-teal-dark">Antrean Rapat Pimpinan</h3>
              <p className="text-xs text-neutral-text-secondary">
                {antreanRapim.length} laporan menunggu penjadwalan RAPIM
              </p>
            </div>
          </div>
          <Link
            to="/rapim"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            Kelola RAPIM
            <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        </div>

        {antreanRapim.length > 0 ? (
          <>
            {/* Table Header */}
            <div className="hidden sm:grid sm:grid-cols-12 gap-3 px-3 py-2 bg-neutral-bg rounded-lg text-[10px] font-semibold uppercase tracking-wider text-neutral-text-secondary mb-2">
              <div className="col-span-2">Kode</div>
              <div className="col-span-4">Judul Laporan</div>
              <div className="col-span-2">Unit</div>
              <div className="col-span-2">Risiko</div>
              <div className="col-span-2 text-right">Diajukan</div>
            </div>

            {/* Table Body - Scrollable */}
            <div className="max-h-[280px] overflow-y-auto space-y-1 pr-1 scrollbar-thin">
              {antreanRapim.map((lap, i) => (
                <Link
                  key={lap.id || i}
                  to={`/laporan/${lap.id}`}
                  className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-3 p-3 rounded-lg border border-purple-100 bg-purple-50/30 hover:bg-purple-50 hover:border-purple-200 transition-all group"
                >
                  {/* Mobile Layout */}
                  <div className="sm:hidden">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-mono text-purple-700 font-semibold">{lap.kode}</span>
                      <Badge variant="risk" risk={(lap.tingkat_risiko || lap.risiko || 'sedang') as TingkatRisiko} />
                    </div>
                    <p className="text-sm font-medium text-neutral-text-primary mb-1 line-clamp-1">{lap.judul}</p>
                    <div className="flex items-center justify-between text-xs text-neutral-text-secondary">
                      <span>{lap.unit?.name || '-'}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {getDaysAgo(lap.created_at)}
                      </span>
                    </div>
                  </div>

                  {/* Desktop Layout */}
                  <div className="hidden sm:flex sm:col-span-2 items-center">
                    <span className="text-xs font-mono text-purple-700 font-semibold">{lap.kode}</span>
                  </div>
                  <div className="hidden sm:flex sm:col-span-4 items-center">
                    <p className="text-sm font-medium text-neutral-text-primary truncate group-hover:text-purple-700 transition-colors">
                      {lap.judul}
                    </p>
                  </div>
                  <div className="hidden sm:flex sm:col-span-2 items-center">
                    <span className="text-xs text-neutral-text-secondary truncate">{lap.unit?.name || '-'}</span>
                  </div>
                  <div className="hidden sm:flex sm:col-span-2 items-center">
                    <Badge variant="risk" risk={(lap.tingkat_risiko || lap.risiko || 'sedang') as TingkatRisiko} />
                  </div>
                  <div className="hidden sm:flex sm:col-span-2 items-center justify-end">
                    <span className="text-xs text-neutral-text-secondary flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(lap.created_at)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            {/* Footer Info */}
            {antreanRapim.length > 5 && (
              <div className="mt-3 pt-3 border-t border-neutral-border flex items-center justify-between">
                <p className="text-xs text-neutral-text-secondary">
                  Menampilkan {Math.min(antreanRapim.length, 10)} dari {kpiData.antrean_rapim} laporan
                </p>
                <Link to="/rapim" className="text-xs font-semibold text-purple-600 hover:underline flex items-center gap-1">
                  Lihat semua <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-10 bg-neutral-bg/30 rounded-xl">
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-3">
              <Gavel className="w-6 h-6 text-purple-400" />
            </div>
            <p className="text-sm font-medium text-neutral-text-primary">Tidak ada laporan dalam antrean</p>
            <p className="text-xs text-neutral-text-secondary mt-1">
              Laporan akan muncul setelah lolos validasi Level 2
            </p>
          </div>
        )}
      </Card>

      {/* ===== KINERJA PER FAKULTAS (REDESIGNED - SCALABLE) ===== */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-main/10 rounded-lg">
              <Users className="w-5 h-5 text-teal-main" />
            </div>
            <div>
              <h3 className="text-base font-bold text-teal-dark">Kinerja per Fakultas</h3>
              <p className="text-xs text-neutral-text-secondary">
                {totalFakultas} unit • Rata-rata penyelesaian {avgCompletionRate}%
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-success"></div>
              <span className="text-neutral-text-secondary">≥80%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-warning"></div>
              <span className="text-neutral-text-secondary">50-79%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-danger"></div>
              <span className="text-neutral-text-secondary">&lt;50%</span>
            </div>
          </div>
        </div>

        {fakultasData.length > 0 ? (
          <>
            {/* Header */}
            <div className="hidden sm:grid sm:grid-cols-12 gap-4 px-4 py-2 bg-neutral-bg rounded-lg text-[10px] font-semibold uppercase tracking-wider text-neutral-text-secondary mb-2">
              <div className="col-span-4">Fakultas / Unit</div>
              <div className="col-span-4">Progress</div>
              <div className="col-span-1 text-center">Total</div>
              <div className="col-span-1 text-center">Selesai</div>
              <div className="col-span-1 text-center">Late</div>
              <div className="col-span-1 text-center">Rate</div>
            </div>

            {/* Scrollable List */}
            <div className="max-h-[320px] overflow-y-auto space-y-2 pr-1 scrollbar-thin">
              {fakultasData.map((fak, i) => {
                const rate = fak.completion_rate || 0;
                const colorClass = rate >= 80 ? 'success' : rate >= 50 ? 'warning' : 'danger';
                const textColorClass = rate >= 80 ? 'text-success' : rate >= 50 ? 'text-warning' : 'text-danger';

                return (
                  <div
                    key={fak.unit_id || i}
                    className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 p-3 sm:px-4 sm:py-3 rounded-lg border border-neutral-border hover:border-teal-main/30 hover:bg-teal-main/5 transition-all"
                  >
                    {/* Mobile Layout */}
                    <div className="sm:hidden">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-neutral-text-primary truncate flex-1 pr-2">
                          {fak.unit}
                        </span>
                        <span className={`text-sm font-black ${textColorClass}`}>{rate}%</span>
                      </div>
                      <ProgressBar value={rate} color={colorClass} size="sm" showLabel={false} />
                      <div className="flex items-center gap-4 mt-2 text-xs text-neutral-text-secondary">
                        <span>{fak.total} total</span>
                        <span className="text-success">{fak.selesai} selesai</span>
                        {fak.overdue > 0 && <span className="text-danger">{fak.overdue} terlambat</span>}
                      </div>
                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden sm:flex sm:col-span-4 items-center">
                      <div className="flex items-center gap-2">
                        <div className={`w-1.5 h-8 rounded-full ${rate >= 80 ? 'bg-success' : rate >= 50 ? 'bg-warning' : 'bg-danger'
                          }`}></div>
                        <span className="text-sm font-medium text-neutral-text-primary truncate">
                          {fak.unit}
                        </span>
                      </div>
                    </div>
                    <div className="hidden sm:flex sm:col-span-4 items-center">
                      <div className="w-full">
                        <ProgressBar value={rate} color={colorClass} size="sm" showLabel={false} />
                      </div>
                    </div>
                    <div className="hidden sm:flex sm:col-span-1 items-center justify-center">
                      <span className="text-sm font-semibold text-neutral-text-primary">{fak.total}</span>
                    </div>
                    <div className="hidden sm:flex sm:col-span-1 items-center justify-center">
                      <span className="text-sm font-semibold text-success">{fak.selesai}</span>
                    </div>
                    <div className="hidden sm:flex sm:col-span-1 items-center justify-center">
                      <span className={`text-sm font-semibold ${fak.overdue > 0 ? 'text-danger' : 'text-neutral-text-secondary'}`}>
                        {fak.overdue}
                      </span>
                    </div>
                    <div className="hidden sm:flex sm:col-span-1 items-center justify-center">
                      <span className={`text-sm font-black ${textColorClass}`}>{rate}%</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary Footer */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-neutral-border">
              <div className="text-center p-2 bg-neutral-bg rounded-lg">
                <p className="text-lg font-black text-teal-main">
                  {fakultasData.reduce((sum, f) => sum + f.total, 0)}
                </p>
                <p className="text-[10px] text-neutral-text-secondary">Total Laporan</p>
              </div>
              <div className="text-center p-2 bg-neutral-bg rounded-lg">
                <p className="text-lg font-black text-success">
                  {fakultasData.reduce((sum, f) => sum + f.selesai, 0)}
                </p>
                <p className="text-[10px] text-neutral-text-secondary">Selesai</p>
              </div>
              <div className="text-center p-2 bg-neutral-bg rounded-lg">
                <p className="text-lg font-black text-danger">
                  {fakultasData.reduce((sum, f) => sum + f.overdue, 0)}
                </p>
                <p className="text-[10px] text-neutral-text-secondary">Terlambat</p>
              </div>
              <div className="text-center p-2 bg-neutral-bg rounded-lg">
                <p className="text-lg font-black text-info">{totalFakultas}</p>
                <p className="text-[10px] text-neutral-text-secondary">Unit</p>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <BarChart3 className="w-12 h-12 mx-auto mb-3 text-neutral-text-secondary opacity-40" />
            <p className="text-sm font-semibold text-neutral-text-primary">Belum ada data kinerja</p>
            <p className="text-xs text-neutral-text-secondary mt-1">
              Data akan muncul setelah ada laporan yang diproses
            </p>
          </div>
        )}
      </Card>

      {/* ===== RINGKASAN PERFORMA (DIPERTAHANKAN) ===== */}
      <Card>
        <h3 className="text-base font-bold text-teal-dark mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Ringkasan Performa
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="p-4 bg-teal-main/5 rounded-xl text-center">
            <p className="text-3xl font-black text-teal-main">
              {kpiData.total_laporan_bidang}
            </p>
            <p className="text-xs text-neutral-text-secondary mt-1">Total Laporan Bidang</p>
          </div>
          <div className="p-4 bg-success/5 rounded-xl text-center">
            <p className="text-3xl font-black text-success">
              {kpiData.total_laporan_bidang > 0
                ? Math.round((kpiData.selesai / kpiData.total_laporan_bidang) * 100)
                : 0}%
            </p>
            <p className="text-xs text-neutral-text-secondary mt-1">Completion Rate</p>
          </div>
          <div className="p-4 bg-info/5 rounded-xl text-center">
            <p className="text-3xl font-black text-info">
              {kpiData.rata_rata_penyelesaian}
            </p>
            <p className="text-xs text-neutral-text-secondary mt-1">Rata-rata Hari</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-xl text-center">
            <p className="text-3xl font-black text-purple-600">
              {kpiData.progress_ppepp}%
            </p>
            <p className="text-xs text-neutral-text-secondary mt-1">Progress PPEPP</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default DashboardWarek;